// An Item's bill of materials and routing on its page (#565): read as on record, and given or corrected in the same
// Form a stopped material check's card offers, by whoever's Roles let them; anyone else reads it. Saving says what LAMP
// did, and an order that waited for it goes on. Whoever's Roles don't let them read it is told so, never that there is
// none (#668).
import { useEffect, useState } from "react";
import type { Api, ItemBomRead } from "../api.js";
import { useWords } from "../i18n/words.js";
import { nameFailure } from "../preview/Failure.js";
import { PartBomForm, PartBomView } from "./PartBomForm.js";

export function ItemBom({
  api,
  item,
}: {
  api: Pick<Api, "itemBom"> & Partial<Pick<Api, "giveItemBom">>;
  item: string;
}) {
  const w = useWords();
  const [read, setRead] = useState<ItemBomRead | "failed">();
  const [busy, setBusy] = useState(false);
  const [said, setSaid] = useState<string>();
  const [problem, setProblem] = useState<string>();
  const [version, setVersion] = useState(0);
  // Read again only for another Item or once saved: the page passes a new `api` object as it renders.
  const { itemBom } = api;
  useEffect(() => {
    let current = true;
    itemBom(item).then(
      (loaded) => {
        if (current) setRead(loaded);
      },
      () => {
        if (current) setRead("failed");
      },
    );
    return () => {
      current = false;
    };
  }, [itemBom, item, version]);
  if (read === undefined) return <p className="muted">…</p>;
  if (read === "failed") return <p className="alert">{w.bomFailed}</p>;
  const give = api.giveItemBom;
  return (
    <>
      {said ? <p role="status">{said}</p> : null}
      {problem ? <p className="alert">{problem}</p> : null}
      {/* Whoever may not read the BOM gets no Form to write over it, whatever else their Roles allow (#893). */}
      {!read.mayRead ? (
        <p className="muted">{w.bomNotYours}</p>
      ) : read.mayRecord && give ? (
        <PartBomForm
          key={version}
          form={read}
          submitLabel={w.bomSave}
          busy={busy}
          onSubmit={(given) => {
            setBusy(true);
            setProblem(undefined);
            give(item, given).then(
              (saved) => {
                setSaid(saved.text);
                setVersion((v) => v + 1);
                setBusy(false);
              },
              (error: unknown) => {
                const failure = nameFailure(error, w);
                setProblem(`${failure.problem} ${failure.recovery}`);
                setBusy(false);
              },
            );
          }}
        />
      ) : (
        <>
          <PartBomView form={read} />
          <p className="small muted">{w.bomReadOnly}</p>
        </>
      )}
    </>
  );
}
